import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createAnnualFeeTypeRequest,
	resetSaveAnnualFeeTypeSuccess,
	updateAnnualFeeTypeRequest,
} from 'redux/reducers/easFee/reducer';
import {
	getAnnualFeeTypeSaveLoading,
	getAnnualFeeTypeSaveSuccess,
} from 'redux/reducers/easFee/selectors';
import { TAnnualFeeTypeDialogProps } from './types';

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	description: Yup.string().max(1000, 'Max 1000 characters'),
	nameForInvoice: Yup.string().max(255, 'Max 255 characters'),
	value: Yup.number().required('Value is required').min(0, 'Must be >= 0'),
	threshold: Yup.number().required('Threshold is required').min(0, 'Must be >= 0'),
});

export const AnnualFeeTypeDialog: FC<TAnnualFeeTypeDialogProps> = ({
	open,
	onClose,
	annualFeeTypeToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!annualFeeTypeToEdit;

	const loading = useSelector(getAnnualFeeTypeSaveLoading);
	const saveSuccess = useSelector(getAnnualFeeTypeSaveSuccess);

	const formik = useFormik({
		initialValues: {
			name: annualFeeTypeToEdit?.name ?? '',
			description: annualFeeTypeToEdit?.description ?? '',
			nameForInvoice: annualFeeTypeToEdit?.nameForInvoice ?? '',
			value: annualFeeTypeToEdit?.value ?? ('' as unknown as number),
			threshold: annualFeeTypeToEdit?.threshold ?? ('' as unknown as number),
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name,
				description: values.description,
				nameForInvoice: values.nameForInvoice,
				value: Number(values.value),
				threshold: Number(values.threshold),
			};
			if (isEdit && annualFeeTypeToEdit) {
				dispatch(updateAnnualFeeTypeRequest({ id: annualFeeTypeToEdit.id, body }));
			} else {
				dispatch(createAnnualFeeTypeRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveAnnualFeeTypeSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{isEdit ? 'Edit Non-Union Subscription' : 'Add New Non-Union Subscription'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<TextField
						label="Description"
						name="description"
						size="small"
						multiline
						minRows={2}
						value={formik.values.description}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.description && !!formik.errors.description}
						helperText={formik.touched.description && formik.errors.description}
					/>
					<TextField
						label="Name For Invoice"
						name="nameForInvoice"
						size="small"
						value={formik.values.nameForInvoice}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.nameForInvoice && !!formik.errors.nameForInvoice}
						helperText={formik.touched.nameForInvoice && formik.errors.nameForInvoice}
					/>
					<TextField
						label="Value"
						name="value"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.value}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.value && !!formik.errors.value}
						helperText={formik.touched.value && formik.errors.value}
					/>
					<TextField
						label="Threshold"
						name="threshold"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.threshold}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.threshold && !!formik.errors.threshold}
						helperText={formik.touched.threshold && formik.errors.threshold}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
